/**
 * RevenueCat Diagnostic Component
 * 
 * Displays detailed information about RevenueCat initialization and available products
 * Use this to diagnose "Payment service not available" errors
 * 
 * Features:
 * - Manual re-initialization with retry button
 * - Shows specific error codes and messages
 * - Displays offerings even if context is not fully initialized
 * - Shows API key being used (masked)
 * - Credit package search across all offerings
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';
import { usePaymentPlatform } from '../contexts/PaymentPlatformContext';
import {
    getOfferings,
    getCustomerInfo,
    isRevenueCatReady,
    getRevenueCatError,
    reinitializeRevenueCat,
    isRevenueCatInitializing,
} from '../services/revenueCatService';
import { isNativePlatform, isIOS, isAndroid } from '../services/platformService';
import { useAuth } from '../contexts/AuthContext';

// Masked API keys for display
const MASKED_IOS_KEY = 'appl_Tfj...Jyz';
const MASKED_ANDROID_KEY = 'goog_dha...IEV';

// Credit package identifiers to search for
const CREDIT_PACKAGE_IDS = [
    'petmatch_10_credits',
    'petmatch_25_credits',
    'petmatch_50_credits',
    'petmatch_100_credits',
];

const formatRevenueCatError = (err: unknown): string => {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;

    const anyErr = err as any;
    const msg =
        (typeof anyErr?.message === 'string' && anyErr.message) ||
        (typeof anyErr?.error?.message === 'string' && anyErr.error.message) ||
        'Unknown error';

    const code = anyErr?.code ?? anyErr?.errorCode ?? anyErr?.error?.code;
    const underlying = anyErr?.underlyingErrorMessage ?? anyErr?.underlyingError;
    const userCancelled = anyErr?.userCancelled;

    let raw = '';
    try {
        raw = JSON.stringify(anyErr, null, 2);
    } catch {
        raw = String(anyErr);
    }

    return [
        msg,
        code ? `code: ${String(code)}` : null,
        typeof userCancelled === 'boolean' ? `userCancelled: ${String(userCancelled)}` : null,
        underlying ? `underlying: ${String(underlying)}` : null,
        raw ? `raw: ${raw}` : null,
    ]
        .filter(Boolean)
        .join('\n');
};

interface DiagnosticData {
    platform: {
        isNative: boolean;
        isIOS: boolean;
        isAndroid: boolean;
        apiKeyUsed: string;
    };
    context: {
        isInitialized: boolean;
        isLoading: boolean;
        hasError: boolean;
        errorMessage: string | null;
    };
    service: {
        isReady: boolean;
        isInitializing: boolean;
        hasServiceError: boolean;
        serviceErrorMessage: string | null;
    };
    userId: string;
    offerings: {
        all: Array<{
            identifier: string;
            isCurrent: boolean;
            packageCount: number;
            packages: Array<{
                identifier: string;
                productId: string;
                price: string;
                title?: string;
            }>;
        }>;
        currentId: string | null;
        totalPackages: number;
        error: string | null;
    };
    packageSearch: {
        searched: string[];
        found: Array<{
            packageId: string;
            offeringId: string;
            productId: string;
            price: string;
        }>;
        notFound: string[];
    };
    customerInfo: {
        hasInfo: boolean;
        originalAppUserId?: string;
        activeEntitlements?: string[];
        allPurchasedProductIds?: string[];
        error?: string;
        reason?: string;
    } | null;
}

export const RevenueCatDiagnostic: React.FC = () => {
    const { user } = useAuth();
    const { isInitialized, isLoading, initializationError, retryInitialization } = usePaymentPlatform();
    const [diagnosticData, setDiagnosticData] = useState<DiagnosticData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isRunningDiagnostic, setIsRunningDiagnostic] = useState(false);

    const runDiagnostic = async () => {
        setError(null);
        setIsRunningDiagnostic(true);

        try {
            console.log('=== REVENUECAT DIAGNOSTIC START ===');

            const data: DiagnosticData = {
                platform: {
                    isNative: isNativePlatform(),
                    isIOS: isIOS(),
                    isAndroid: isAndroid(),
                    apiKeyUsed: isIOS() ? MASKED_IOS_KEY : MASKED_ANDROID_KEY,
                },
                context: {
                    isInitialized,
                    isLoading,
                    hasError: !!initializationError,
                    errorMessage: initializationError?.message || null,
                },
                service: {
                    isReady: isRevenueCatReady(),
                    isInitializing: isRevenueCatInitializing(),
                    hasServiceError: !!getRevenueCatError(),
                    serviceErrorMessage: getRevenueCatError()?.message || null,
                },
                userId: user?.id || 'NOT_LOGGED_IN',
                offerings: {
                    all: [],
                    currentId: null,
                    totalPackages: 0,
                    error: null,
                },
                packageSearch: {
                    searched: CREDIT_PACKAGE_IDS,
                    found: [],
                    notFound: [],
                },
                customerInfo: null,
            };

            // Try to get offerings regardless of initialization state
            if (isNativePlatform()) {
                try {
                    const offerings = await getOfferings();
                    console.log('Full offerings response:', offerings);

                    if (offerings) {
                        // Get all offerings (not just current)
                        const allOfferings = offerings.all || {};
                        let totalPackages = 0;

                        data.offerings.all = Object.keys(allOfferings).map(key => {
                            const offering = allOfferings[key];
                            const packages = offering?.availablePackages?.map((pkg: any) => ({
                                identifier: pkg.identifier,
                                productId: pkg.product?.identifier || 'unknown',
                                price: pkg.product?.priceString || 'N/A',
                                title: pkg.product?.title,
                            })) || [];
                            
                            totalPackages += packages.length;

                            return {
                                identifier: key,
                                isCurrent: key === offerings.current?.identifier,
                                packageCount: packages.length,
                                packages,
                            };
                        });

                        data.offerings.currentId = offerings.current?.identifier || null;
                        data.offerings.totalPackages = totalPackages;

                        // Search for credit packages across ALL offerings
                        CREDIT_PACKAGE_IDS.forEach(pkgId => {
                            let found = false;
                            Object.keys(allOfferings).forEach(offeringKey => {
                                const offering = allOfferings[offeringKey];
                                const pkg = offering?.availablePackages?.find((p: any) => p.identifier === pkgId);
                                if (pkg) {
                                    data.packageSearch.found.push({
                                        packageId: pkgId,
                                        offeringId: offeringKey,
                                        productId: pkg.product?.identifier || 'unknown',
                                        price: pkg.product?.priceString || 'N/A',
                                    });
                                    found = true;
                                }
                            });
                            if (!found) {
                                data.packageSearch.notFound.push(pkgId);
                            }
                        });
                    } else {
                        data.offerings.error = 'Offerings response was null or undefined';
                    }
                } catch (err: any) {
                    data.offerings.error = formatRevenueCatError(err);
                    console.error('Error fetching offerings:', err);
                }

                // Try to get customer info
                try {
                    const customerInfo = await getCustomerInfo();
                    console.log('Customer info response:', customerInfo);

                    if (customerInfo) {
                        data.customerInfo = {
                            hasInfo: true,
                            originalAppUserId: customerInfo.originalAppUserId,
                            activeEntitlements: Object.keys(customerInfo.entitlements?.active || {}),
                            allPurchasedProductIds: customerInfo.allPurchasedProductIdentifiers || [],
                        };
                    } else {
                        data.customerInfo = {
                            hasInfo: false,
                            reason: isInitialized ? 'SDK returned null customer info' : 'SDK not initialized',
                        };
                    }
                } catch (err: any) {
                    data.customerInfo = {
                        hasInfo: false,
                        error: formatRevenueCatError(err),
                    };
                }
            }

            console.log('Diagnostic data:', data);
            console.log('=== REVENUECAT DIAGNOSTIC END ===');

            setDiagnosticData(data);
        } catch (err: any) {
            console.error('Diagnostic failed:', err);
            setError(formatRevenueCatError(err));
        } finally {
            setIsRunningDiagnostic(false);
        }
    };

    const handleRetryInitialization = async () => {
        setIsRetrying(true);
        try {
            await retryInitialization();
            await runDiagnostic();
        } catch (err: any) {
            setError(`Retry failed: ${err.message}`);
        } finally {
            setIsRetrying(false);
        }
    };

    const handleForceReinitialize = async () => {
        setIsRetrying(true);
        try {
            await reinitializeRevenueCat(user?.id);
            await runDiagnostic();
        } catch (err: any) {
            setError(`Reinitialize failed: ${err.message}`);
        } finally {
            setIsRetrying(false);
        }
    };

    useEffect(() => {
        if (isNativePlatform() && !isLoading) {
            runDiagnostic();
        }
    }, [isInitialized, isLoading]);

    if (!isNativePlatform()) {
        return (
            <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                    RevenueCat diagnostic is only available on native platforms (iOS/Android)
                </AlertDescription>
            </Alert>
        );
    }

    const getStatusBadge = (isOk: boolean, label: string) => (
        <Badge variant={isOk ? 'default' : 'destructive'} className="flex items-center gap-1">
            {isOk ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {label}
        </Badge>
    );

    return (
        <Card className="w-full max-w-4xl mx-auto my-4">
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <span>RevenueCat Diagnostic</span>
                    <div className="flex gap-2">
                        {getStatusBadge(isInitialized, isInitialized ? 'Initialized' : 'Not Initialized')}
                        {diagnosticData && diagnosticData.offerings.totalPackages > 0 && (
                            <Badge variant="secondary">{diagnosticData.offerings.totalPackages} Products</Badge>
                        )}
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                    <Button 
                        onClick={runDiagnostic} 
                        disabled={isLoading || isRunningDiagnostic}
                        variant="outline"
                        size="sm"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${isRunningDiagnostic ? 'animate-spin' : ''}`} />
                        Run Diagnostic
                    </Button>
                    <Button 
                        onClick={handleRetryInitialization} 
                        disabled={isRetrying || isLoading}
                        variant="secondary"
                        size="sm"
                    >
                        <RefreshCw className={`h-4 w-4 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
                        Retry Initialization
                    </Button>
                    <Button 
                        onClick={handleForceReinitialize} 
                        disabled={isRetrying || isLoading}
                        variant="destructive"
                        size="sm"
                    >
                        Force Reinitialize
                    </Button>
                </div>

                {/* Quick Status Summary */}
                {diagnosticData && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div className="p-2 bg-muted rounded">
                            <div className="text-muted-foreground">Platform</div>
                            <div className="font-medium">{diagnosticData.platform.isIOS ? 'iOS' : 'Android'}</div>
                        </div>
                        <div className="p-2 bg-muted rounded">
                            <div className="text-muted-foreground">SDK Ready</div>
                            <div className="font-medium">{diagnosticData.service.isReady ? '✅ Yes' : '❌ No'}</div>
                        </div>
                        <div className="p-2 bg-muted rounded">
                            <div className="text-muted-foreground">Total Packages</div>
                            <div className="font-medium">{diagnosticData.offerings.totalPackages}</div>
                        </div>
                        <div className="p-2 bg-muted rounded">
                            <div className="text-muted-foreground">User ID</div>
                            <div className="font-medium truncate text-xs">{diagnosticData.userId?.slice(0, 8) || 'N/A'}...</div>
                        </div>
                    </div>
                )}

                {error && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Initialization Error */}
                {(initializationError || diagnosticData?.service.hasServiceError) && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="font-semibold">Initialization Error</div>
                            <div className="text-sm mt-1">
                                {initializationError?.message || diagnosticData?.service.serviceErrorMessage}
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {/* Credentials Issue Warning - Specific for Android */}
                {diagnosticData?.offerings.error?.includes('credentials') && diagnosticData.platform.isAndroid && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="font-semibold">🔐 Android Credentials Issue Detected</div>
                            <div className="text-sm mt-2 space-y-2">
                                <p>RevenueCat cannot communicate with Google Play. This is a <strong>backend configuration issue</strong>, not a code issue.</p>
                                <div className="mt-3 p-3 bg-background rounded text-xs space-y-2">
                                    <p className="font-semibold">Required Steps in RevenueCat Dashboard:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Go to RevenueCat → Project Settings → Apps → Android App</li>
                                        <li>Upload your <strong>Google Play Service Account JSON key</strong></li>
                                        <li>Verify the Service Account has "Financial Data" permission in Google Play Console</li>
                                        <li>Wait 24-48 hours for Google to propagate permissions</li>
                                    </ol>
                                </div>
                                <div className="mt-3 p-3 bg-background rounded text-xs space-y-2">
                                    <p className="font-semibold">Required Steps in Google Play Console:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Go to Google Play Console → API Access</li>
                                        <li>Create or link a Service Account</li>
                                        <li>Grant permissions: View financial data, View app info</li>
                                        <li>Download the JSON key and upload to RevenueCat</li>
                                    </ol>
                                </div>
                                <div className="mt-3 p-3 bg-background rounded text-xs">
                                    <p className="font-semibold">Also verify:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>App is installed via Internal/Closed Testing track (NOT debug APK)</li>
                                        <li>Test email is in Google Play Console → License Testing</li>
                                        <li>Package name matches: <code className="bg-muted px-1">io.petius.app</code></li>
                                    </ul>
                                </div>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {/* Credentials Issue Warning - iOS */}
                {diagnosticData?.offerings.error?.includes('credentials') && diagnosticData.platform.isIOS && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="font-semibold">🔐 iOS Credentials Issue Detected</div>
                            <div className="text-sm mt-2 space-y-2">
                                <p>RevenueCat cannot communicate with App Store Connect.</p>
                                <div className="mt-3 p-3 bg-background rounded text-xs space-y-2">
                                    <p className="font-semibold">Required Steps:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Verify App Store Connect Shared Secret is configured in RevenueCat</li>
                                        <li>Check Bundle ID matches: <code className="bg-muted px-1">io.petius.app</code></li>
                                        <li>Ensure products are in "Ready to Submit" status in App Store Connect</li>
                                        <li>Products must be linked to the app version in App Store Connect</li>
                                    </ol>
                                </div>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {/* No Packages Warning (Generic) */}
                {diagnosticData?.offerings.totalPackages === 0 && !diagnosticData?.offerings.error && (
                    <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <div className="font-semibold">⚠️ No Products Available</div>
                            <div className="text-sm mt-2 space-y-1">
                                <p>This usually means one of the following:</p>
                                <ul className="list-disc list-inside text-xs space-y-1 mt-2">
                                    <li>The app is NOT installed from Play Store/TestFlight (using debug APK)</li>
                                    <li>Your test email is NOT in Google Play Console → License Testing</li>
                                    <li>Products are not yet approved in App Store Connect / Play Console</li>
                                    <li>RevenueCat API key is incorrect or project not linked</li>
                                    <li>App is not signed with the correct certificate</li>
                                </ul>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}

                {diagnosticData && (
                    <div className="space-y-4 font-mono text-xs">
                        {/* Platform & Context Status */}
                        <div className="p-4 bg-muted rounded">
                            <h3 className="font-bold mb-2">Platform & Context Status</h3>
                            <pre className="overflow-auto">{JSON.stringify({
                                ...diagnosticData.platform,
                                ...diagnosticData.context,
                                ...diagnosticData.service,
                            }, null, 2)}</pre>
                        </div>

                        {/* All Offerings */}
                        <div className={`p-4 rounded ${diagnosticData.offerings.totalPackages > 0 ? 'bg-muted' : 'bg-destructive/10'}`}>
                            <h3 className="font-bold mb-2">All Offerings</h3>
                            {diagnosticData.offerings.error ? (
                                <pre className="text-destructive overflow-auto">
                                    Error: {diagnosticData.offerings.error}
                                </pre>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <strong>Total Offerings:</strong> {diagnosticData.offerings.all.length}
                                    </div>
                                    {diagnosticData.offerings.all.map((offering, idx) => (
                                        <div key={idx} className="mb-4 p-3 bg-background rounded">
                                            <div className="flex items-center gap-2 mb-2">
                                                <strong>{offering.identifier}</strong>
                                                {offering.isCurrent && (
                                                    <Badge className="bg-primary">CURRENT</Badge>
                                                )}
                                            </div>
                                            <div className="mb-2">
                                                <strong>Packages:</strong> {offering.packageCount}
                                            </div>
                                            {offering.packages.length > 0 && (
                                                <div className="ml-4 space-y-1">
                                                    {offering.packages.map((pkg, pidx) => (
                                                        <div key={pidx} className="text-xs">
                                                            • {pkg.identifier} → {pkg.productId} ({pkg.price})
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* Package Search Results */}
                        <div className="p-4 bg-muted rounded">
                            <h3 className="font-bold mb-2">Credit Package Search</h3>
                            <div className="mb-3">
                                <strong>Searched for:</strong>
                                <div className="ml-4 mt-1">
                                    {diagnosticData.packageSearch.searched.map((id, idx) => (
                                        <div key={idx} className="text-xs">• {id}</div>
                                    ))}
                                </div>
                            </div>

                            {diagnosticData.packageSearch.found.length > 0 && (
                                <div className="mb-3">
                                    <strong className="text-primary">✓ Found ({diagnosticData.packageSearch.found.length}):</strong>
                                    <div className="ml-4 mt-1 space-y-1">
                                        {diagnosticData.packageSearch.found.map((item, idx) => (
                                            <div key={idx} className="text-xs text-primary">
                                                • {item.packageId} in "{item.offeringId}" → {item.productId} ({item.price})
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {diagnosticData.packageSearch.notFound.length > 0 && (
                                <div>
                                    <strong className="text-destructive">✗ Not Found ({diagnosticData.packageSearch.notFound.length}):</strong>
                                    <div className="ml-4 mt-1">
                                        {diagnosticData.packageSearch.notFound.map((id, idx) => (
                                            <div key={idx} className="text-xs text-destructive">
                                                • {id}
                                            </div>
                                        ))}
                                    </div>
                                    <Alert variant="destructive" className="mt-3">
                                        <AlertDescription>
                                            ⚠️ These packages are NOT in any offering! Check RevenueCat Dashboard.
                                        </AlertDescription>
                                    </Alert>
                                </div>
                            )}
                        </div>

                        {/* Customer Info */}
                        <div className={`p-4 rounded ${diagnosticData.customerInfo?.hasInfo ? 'bg-muted' : 'bg-muted/50'}`}>
                            <h3 className="font-bold mb-2">Customer Info</h3>
                            <pre className="overflow-auto">{JSON.stringify(diagnosticData.customerInfo, null, 2)}</pre>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
